import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { Comment } from './types.js';

type NewComment = {
  body: string;
  internal?: boolean;
  attachment?: { filename: string; contentType: string; dataBase64: string };
};

export function useStoryComments(projectId: string, storyId: string) {
  return useQuery({
    queryKey: ['comments', projectId, storyId],
    queryFn: () =>
      apiFetch<{ comments: Comment[] }>(`/projects/${projectId}/stories/${storyId}/comments`).then(
        (r) => r.comments,
      ),
  });
}

export function useAddStoryComment(projectId: string, storyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewComment) =>
      apiFetch<{ comment: Comment }>(`/projects/${projectId}/stories/${storyId}/comments`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      // A story comment also shows up in the general messaging feed.
      void qc.invalidateQueries({ queryKey: ['comments', projectId, storyId] });
      void qc.invalidateQueries({ queryKey: ['messages', projectId] });
    },
  });
}

export function useSpecComments(projectId: string) {
  return useQuery({
    queryKey: ['spec-comments', projectId],
    queryFn: () =>
      apiFetch<{ comments: Comment[] }>(`/projects/${projectId}/specification/comments`).then(
        (r) => r.comments,
      ),
  });
}

export function useAddSpecComment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewComment) =>
      apiFetch<{ comment: Comment }>(`/projects/${projectId}/specification/comments`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['spec-comments', projectId] });
      void qc.invalidateQueries({ queryKey: ['messages', projectId] });
    },
  });
}

export function useProjectMessages(projectId: string) {
  return useQuery({
    queryKey: ['messages', projectId],
    queryFn: () =>
      apiFetch<{ messages: Comment[]; lastReadAt: string | null }>(
        `/projects/${projectId}/messages`,
      ),
  });
}

export function useAddProjectMessage(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewComment) =>
      apiFetch<{ comment: Comment }>(`/projects/${projectId}/messages`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', projectId] }),
  });
}
